import { useTickDatumGetterForDate } from '@/components/common/charts/chartHelper';
import {
    ChartTooltip,
    ChartTooltipLine,
    defaultChartTooltipWidth,
} from '@/components/common/charts/ChartTooltip';
import CursorTracker from '@/components/common/charts/CursorTracker';
import DatesXAxis from '@/components/common/charts/DatesXAxis';
import { EntityLine } from '@/components/common/charts/EntityLine';
import NumericYAxisWithGrid from '@/components/common/charts/NumericYAxisWithGrid';
import { TodayLine, useTodayX } from '@/components/common/charts/TodayLine';
import { useTickRangeNumericChartWithTooltip } from '@/components/common/charts/useTickRangeNumericChartWithTooltip';
import { FlexRow } from '@/components/common/Flex';
import { FlexProps } from '@chakra-ui/react';
import { useCurrentUser } from '@common/react/user';
import { toPx } from '@common/util/styles';
import { nowTick, Tick, TickRange, ValueSeries } from '@common/valueSeries';
import { Group } from '@visx/group';
import isEqual from 'lodash.isequal';
import { memo, useCallback, useMemo } from 'react';

export const CHART_MARGIN = { top: 10, right: 10, bottom: 40, left: 60 };
export const CHART_CONTAINER_STYLES: FlexProps = {
    h: 60,
    minH: 60,
    maxH: 60,
    w: 'full',
    position: 'relative',
    flexBasis: 60,
    flexGrow: 0,
    flexShrink: 0,
    mb: 6,
};

export interface DashboardLineChartProps<Datum extends Tick> {
    valueSeries: ValueSeries<Datum>;
    range: TickRange;
    width: number;
    height: number;
    yValueGetter: (d?: Datum) => number | null;
    color: string;
    tooltipProps: {
        renderTitle: (d: Datum) => string;
        renderBody: (d: Datum) => JSX.Element;
        renderHeaderRight: (d: Datum) => JSX.Element;
    };
}

const DashboardLineChartInner = <D extends Tick>({
    valueSeries,
    range,
    width,
    height,
    yValueGetter,
    tooltipProps,
    color,
}: DashboardLineChartProps<D>) => {
    const { userInfo } = useCurrentUser();

    // Set dimensions, construct scales

    const innerWidth = width - CHART_MARGIN.left - CHART_MARGIN.right;
    const innerHeight = height - CHART_MARGIN.top - CHART_MARGIN.bottom;

    const getDatumForDate = useTickDatumGetterForDate(valueSeries, (vn) => vn);
    const maxValueForDate = useCallback(
        (date: string) => yValueGetter(getDatumForDate(date)) ?? 0,
        [getDatumForDate, yValueGetter]
    );
    const tooltipDataForDate = useCallback(
        (date: string) => getDatumForDate(date),
        [getDatumForDate]
    );

    const [series, min, max] = useMemo(() => {
        const todayTick = nowTick(range.unit);
        const todayTickDate = todayTick.date;

        let min = Infinity;
        let max = -Infinity;

        const series: {
            recordedData: D[];
            forecastData: D[];
        } = {
            recordedData: [],
            forecastData: [],
        };
        for (const vn of valueSeries.values) {
            const d = getDatumForDate(vn.date);
            const dValue = yValueGetter(d) ?? 0;
            if (!d) continue;
            if (d.date >= todayTickDate) series.forecastData.push(d);
            if (d.date <= todayTickDate) series.recordedData.push(d);
            max = Math.max(dValue, max);
            min = Math.min(dValue, min);
        }
        return [series, min, max];
    }, [valueSeries, getDatumForDate, range.unit, yValueGetter]);

    const { xScale, yScale, tooltip, handleCursor, handleCursorLeave } =
        useTickRangeNumericChartWithTooltip<D>({
            range,
            yScaleDomain: [min, max],
            maxValueForDate,
            tooltipDataForDate,
            innerHeight,
            innerWidth,
            margin: CHART_MARGIN,
            shouldClampY: false,
            tooltipXBuffer: defaultChartTooltipWidth / 1.5,
        });

    const todayX = useTodayX({ range, xScale });

    // memoized point getters for entity line
    const xGetter = useCallback((d: D) => xScale(d.date) ?? 0, [xScale]);
    const yGetter = useCallback(
        (d: D) => yScale(yValueGetter(d) ?? min),
        [yScale, yValueGetter, min]
    );

    if (innerWidth < 0 || innerHeight < 0 || !userInfo) return null;

    return (
        <FlexRow w={width} h={height} onMouseOut={handleCursorLeave}>
            <svg width={width} height={height}>
                <Group left={CHART_MARGIN.left} top={CHART_MARGIN.top}>
                    <NumericYAxisWithGrid
                        scale={yScale}
                        width={innerWidth}
                        height={innerHeight}
                    />
                    <DatesXAxis
                        top={innerHeight}
                        scale={xScale}
                        unit={range.unit}
                    />
                    {series.recordedData.length && (
                        <EntityLine
                            data={series.recordedData}
                            x={xGetter}
                            y={yGetter}
                            color={color}
                            yScale={yScale}
                            solid
                        />
                    )}
                    {series.forecastData.length && (
                        <EntityLine
                            data={series.forecastData}
                            x={xGetter}
                            y={yGetter}
                            color={color}
                            yScale={yScale}
                        />
                    )}

                    <TodayLine todayX={todayX} innerHeight={innerHeight} />
                    <ChartTooltipLine
                        {...tooltip}
                        color={color}
                        innerHeight={innerHeight}
                    />
                    <CursorTracker
                        w={innerWidth}
                        h={innerHeight}
                        onCursor={handleCursor}
                        onCursorLeave={handleCursorLeave}
                    />
                </Group>
            </svg>

            {/* Render tooltip outside the svg for full html support */}
            {tooltip.visible && tooltip.data && (
                <ChartTooltip
                    key={`chart-tooltip-${tooltip.data.date}`}
                    top={1}
                    left={toPx(tooltip.left)}
                    title={tooltipProps.renderTitle(tooltip.data)}
                    headerRight={tooltipProps.renderHeaderRight(tooltip.data)}
                >
                    {tooltipProps.renderBody(tooltip.data)}
                </ChartTooltip>
            )}
        </FlexRow>
    );
};

const DashboardLineChart = memo(
    DashboardLineChartInner,
    (prevProps, nextProps) => {
        return (
            prevProps.valueSeries === nextProps.valueSeries &&
            prevProps.height === nextProps.height &&
            prevProps.width === nextProps.width &&
            isEqual(prevProps.range, nextProps.range)
        );
    }
) as typeof DashboardLineChartInner;

export default DashboardLineChart;
